<script setup>
import {onMounted, ref, provide, computed} from "vue";
import {useSupplierStockStore} from "@/stores/supplierStock.js";
import {useUserStore} from "@/stores/user.js";
import {showMessage} from "@/utils/showMessage.js";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {Icon} from "@iconify/vue";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {Button} from "primevue";
import showAlert from "@/utils/showAlert.js";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import AddMenu from "@/views/stocks/supplier/settings/Add.vue";
import SortIcon from "@/components/datatable/sortIcon.vue";
import AllButtons from "@/views/orders/Shared/AllButtons.vue";
import {useFilterArray} from "@/composables/useFilterArray.js";

const supplierStockStore = useSupplierStockStore();
const userStore = useUserStore();
const loading = ref(false)
const deleting = ref(false)
const menus = ref(null)
const isShowAddMenuModal = ref(false)
const hasSettingsAccess = computed(() => userStore.hasPermission("STOCKS_MANAGER_ACCESS") || userStore.hasPermission('BIG_BOSS'))
const selectedRecord = ref(null)
const keyword = ref('')
const fetchInitialData = async (refresh) => {
  try {
    if (!refresh) loading.value = true
    const res = await supplierStockStore.action_getMenu();
    if (res.result === true) {
      menus.value = res.data
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    loading.value = false
  }
}

const removeMenu = async (data) => {
  const ok = await showAlert(`You are deleting Menu (${data.game} - ${data.server})`, 'Are you sure?', 'Yes', 'No')
  if (!ok) return

  try {
    deleting.value = true
    const res = await supplierStockStore.action_removeMenu({
      "game": data.game,
      "server": data.server
    })
    if (res.result === true) {
      showMessage('Menu deleted!', 'success')
      await fetchInitialData(true)
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    deleting.value = false
  }
}

function showAddMenuModal() {
  isShowAddMenuModal.value = true
}

const {filtered: filteredMenus} = useFilterArray(
    menus,
    keyword,
    'menus'
);

onMounted(() => {
  fetchInitialData()
})

provide('isShowAddMenuModal', isShowAddMenuModal)
</script>

<template>
  <div class="flex flex-col gap-4 w-full h-full">
    <div class="flex flex-row justify-between gap-4 w-full">
      <Breadcrumbs/>
      <AllButtons :show-filter="false" :showCategoriesButtons="false" :copy-button="false"
                  v-model="keyword" :button-for-new-item="true"
                  title-for-new-item="Add Menu" permission-for-new-item="STOCKS_MANAGER_ACCESS"
                  @button-for-new-item-clicked="showAddMenuModal"/>
    </div>
    <div v-if="loading" class="flex items-center justify-center h-60">
      <Icon icon="eos-icons:bubble-loading" class="size-12"/>
    </div>
    <DataTable v-else
               :nullSortOrder="-1"
               :value="filteredMenus"
               scrollable
               scrollHeight="flex"
               :virtualScrollerOptions="{ itemSize: 30 }"
               resizableColumns
               removableSort
               stripedRows
               class="text-xs w-full"
               size="small"
               tableStyle="width: 100%; table-layout: fixed;"
               row-hover
               :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300'"
    >
      <Column field="game" header="Game" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 10%; min-width: 70px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder"/>
        </template>
      </Column>
      <Column field="server" header="Server" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: max-content; min-width: 70px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder"/>
        </template>
      </Column>
      <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 8%; min-width: 100px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder"/>
        </template>
        <template #body="slotProps">
          <span class="block w-full max-w-max">
            {{ gregorianDate(slotProps.data.lastUpdate, 'DD MMM HH:mm') }}
          </span>
        </template>
      </Column>
      <Column :exportable="false"
              style="width: 5%; min-width: 70px; height: 30px" v-if="hasSettingsAccess">
        <template #body="slotProps">
          <div class="flex flex-row gap-2 justify-end items-center w-full">
            <button @click.stop="removeMenu(slotProps.data)">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                   class="lucide lucide-trash2-icon lucide-trash-2">
                <path d="M10 11v6"/>
                <path d="M14 11v6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                <path d="M3 6h18"/>
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
          </div>
        </template>
      </Column>
    </DataTable>
    <AddMenu v-if="isShowAddMenuModal" @refresh="fetchInitialData" :selected-record="selectedRecord"/>
  </div>
</template>